import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import './DealForm.css';
import axios from 'axios';
import { toast } from 'react-toastify';

const DealForm = ({ user, goBack, fetchDeals, deal, isEdit }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image: '',
    imageFile: null,
    price: '',
    startDate: '',
    endDate: '',
    categories: [],
    dayOfWeek: [],
    type: [],
    link: '',
  });
  const [newImageKey, setNewImageKey] = useState(null); // ← NEW: stores S3 key for thumbnail generation
  const [noEndDate, setNoEndDate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [categoryOptions, setCategoryOptions] = useState([]);

  const dayOfWeekOptions = [
    { value: 'Monday', label: 'Monday' },
    { value: 'Tuesday', label: 'Tuesday' },
    { value: 'Wednesday', label: 'Wednesday' },
    { value: 'Thursday', label: 'Thursday' },
    { value: 'Friday', label: 'Friday' },
    { value: 'Saturday', label: 'Saturday' },
    { value: 'Sunday', label: 'Sunday' },
  ];

  const typeOptions = [
    { value: 'Dine-In', label: 'Dine-In' },
    { value: 'Takeaway', label: 'Takeaway' },
    { value: 'Delivery', label: 'Delivery' },
  ];

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/categories`, {
          headers: { 'Authorization': `Bearer ${user.token}` },
        });
        if (!response.ok) throw new Error('Failed to fetch categories');
        const data = await response.json();
        setCategoryOptions(data.map(cat => ({ value: cat._id.toString(), label: cat.name })));
      } catch (err) {
        console.error('Error fetching categories:', err);
        setError('Failed to load categories');
      }
    };
    if (user && user.token) fetchCategories();
  }, [user]);

  useEffect(() => {
    if (isEdit && deal) {
      const categoryIds = (deal.category || []).map(cat => (typeof cat === 'object' ? cat._id.toString() : cat.toString()));
      const endDateStr = deal.endDate ? new Date(deal.endDate).toISOString().split('T')[0] : '';

      setFormData({
        title: deal.title || '',
        description: deal.description || '',
        image: deal.image || '',
        imageFile: null,
        price: deal.price != null ? deal.price.toString() : '',
        startDate: deal.startDate ? new Date(deal.startDate).toISOString().split('T')[0] : '',
        endDate: endDateStr,
        categories: categoryIds,
        dayOfWeek: deal.dayOfWeek || [],
        type: deal.type || [],
        link: deal.link || '',
      });

      // Auto-check if existing end date is far in future
      if (endDateStr) {
        const end = new Date(endDateStr);
        const twoYearsFromNow = new Date();
        twoYearsFromNow.setFullYear(twoYearsFromNow.getFullYear() + 2);
        if (end > twoYearsFromNow) {
          setNoEndDate(true);
        }
      }
    }
  }, [deal, isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    if (name === 'title') {
      if (value.length > 50) {
        newValue = value.slice(0, 50);
        toast.info('Title limited to 50 characters', { autoClose: 2200 });
      }
    } else if (name === 'description') {
      if (value.length > 300) {
        newValue = value.slice(0, 300);
        toast.info('Description limited to 300 characters', { autoClose: 2200 });
      }
    } else if (name === 'price') {
      let cleaned = value
        .replace(/[^0-9.]/g, '')
        .replace(/(\..*)\./g, '$1');

      const parts = cleaned.split('.');
      if (parts[1]) {
        parts[1] = parts[1].slice(0, 2);
      }
      newValue = parts.join('.');
    }

    setFormData((prev) => ({ ...prev, [name]: newValue }));
  };

  const handleNoEndDateChange = (e) => {
    const checked = e.target.checked;
    setNoEndDate(checked);

    if (checked) {
      const twoYearsLater = new Date();
      twoYearsLater.setFullYear(twoYearsLater.getFullYear() + 2);
      const formatted = twoYearsLater.toISOString().split('T')[0];
      setFormData((prev) => ({ ...prev, endDate: formatted }));
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        setError('Only JPEG and PNG images are allowed');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }

      try {
        // Get presigned URL
        const presigned = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/deals/presigned-url`,
          {
            fileType: file.type,
            fileName: file.name,
            type: 'deal',
          },
          { headers: { Authorization: `Bearer ${user.token}` } }
        );

        const { url, key } = presigned.data;

        // Upload file directly to S3
        await axios.put(url, file, {
          headers: { 'Content-Type': file.type },
        });

        const cloudfrontUrl = `https://${process.env.REACT_APP_CLOUDFRONT_DOMAIN || 'd2gga4z6dbq9nq.cloudfront.net'}/${key}`;

        setFormData(prev => ({
          ...prev,
          imageFile: null,
          image: cloudfrontUrl,
        }));
        setNewImageKey(key); // Store key for backend thumbnail generation
        setError('');
        toast.success('Image uploaded successfully');
      } catch (err) {
        console.error('Image upload error:', err);
        setError('Failed to upload image');
        toast.error('Failed to upload image');
      }
    }
  };

  const handleMultiSelectChange = (selectedOptions, field) => {
    const values = selectedOptions ? selectedOptions.map((opt) => opt.value) : [];
    setFormData((prev) => ({
      ...prev,
      [field]: values,
    }));
  };

  const isValidUrl = (url) => {
    const pattern = /^(https?:\/\/)?([\w.-]+)\.([a-z]{2,})(\/[\w.-]*)*(\?[\w-=&]*)?(#[\w-]*)?$/i;
    return pattern.test(url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.link && !isValidUrl(formData.link)) {
      setError('Please enter a valid URL (e.g., https://example.com)');
      return;
    }
    setLoading(true);
    try {
      const numericPrice = formData.price.trim() === '' ? 0 : parseFloat(formData.price);

      const url = isEdit ? `${process.env.REACT_APP_API_URL}/api/deals/${deal._id}` : `${process.env.REACT_APP_API_URL}/api/deals`;
      const method = isEdit ? 'PUT' : 'POST';

      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        image: formData.image,
        price: numericPrice,
        startDate: formData.startDate,
        endDate: formData.endDate,
        category: formData.categories,
        dayOfWeek: formData.dayOfWeek,
        type: formData.type,
        link: formData.link.trim(),
        imageKey: newImageKey || undefined, // Send only if new image uploaded
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to ${isEdit ? 'update' : 'create'} deal`);
      }

      await fetchDeals();
      toast.success(isEdit ? 'Deal updated successfully' : 'Deal created successfully');
      setNewImageKey(null); // Clear after success
      goBack();
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const dropdownStyles = {
    control: (provided) => ({
      ...provided,
      width: '100%',
      padding: '5px',
      border: '2px solid #007AFF',
      borderRadius: '5px',
      backgroundColor: '#2E2E2E',
      color: '#FFFFFF',
      fontSize: '16px',
      boxShadow: 'none',
      '&:hover': { borderColor: '#40C4FF' },
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#FFFFFF',
      opacity: 0.7,
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: 'transparent',
      border: 'none',
      margin: '2px 4px',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: '#FFFFFF',
      padding: '0 4px',
      fontSize: '14px',
      textDecoration: 'underline',
      textDecorationColor: '#40C4FF',
      textDecorationThickness: '1px',
      '&:hover': {
        textDecorationColor: '#FF4500',
      },
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#FF4500',
      '&:hover': { backgroundColor: 'transparent', color: '#FFFFFF' },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: '#2E2E2E',
      border: '2px solid #007AFF',
      borderRadius: '5px',
      maxHeight: '200px',
      overflowY: 'auto',
    }),
    option: (provided, state) => ({
      ...provided,
      padding: '5px 10px',
      color: '#FFFFFF',
      backgroundColor: state.isFocused ? '#007AFF' : '#2E2E2E',
      '&:hover': { backgroundColor: '#007AFF' },
    }),
    menuList: (provided) => ({
      ...provided,
      '::-webkit-scrollbar': {
        width: '8px',
      },
      '::-webkit-scrollbar-track': {
        backgroundColor: '#2E2E2E',
        borderRadius: '10px',
      },
      '::-webkit-scrollbar-thumb': {
        backgroundColor: '#40C4FF',
        borderRadius: '10px',
      },
      '::-webkit-scrollbar-thumb:hover': {
        backgroundColor: '#FF4500',
      },
    }),
  };

  if (loading) return <div className="loading">Processing...</div>;

  const titleLength = formData.title.length;
  const descLength = formData.description.length;

  return (
    <div className="deal-form">
      <h2>{isEdit ? 'Edit Deal' : 'Add New Deal'}</h2>
      {error && <p className="error">{error}</p>}
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Title (max 50 characters)</label>
          <div className="input-container">
            <input
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              placeholder="Enter deal title"
            />
          </div>
          <span className="char-count">{formData.title.length}/50</span>
        </div>

        <div className="form-group">
          <label>Description (max 300 characters)</label>
          <div className="input-container description-container">
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              placeholder="Enter deal description"
              rows="3"
            />
          </div>
          <span className="char-count">{formData.description.length}/300</span>
        </div>

        <div className="form-group">
          <label>Deal Image</label>
          {formData.image && (
            <div className="image-preview">
              <img
                src={formData.image}
                alt="Deal Image"
                className="deal-image-preview"
                onError={(e) => (e.target.style.display = 'none')}
              />
            </div>
          )}
          <div className="input-wrapper">
            <input
              type="file"
              accept="image/jpeg,image/png"
              onChange={handleFileChange}
              className="form-input"
            />
            <div className="tooltip">
              Recommended size: 600x400px (3:2), ~100-200KB
            </div>
          </div>
        </div>

        <div className="form-group">
          <label>Price <span className="price-suggestion">(Or price from lowest priced item)</span></label>
          <div className="price-input-wrapper">
            <input
              name="price"
              value={formData.price}
              onChange={handleChange}
              placeholder="0.00"
              type="text"
              inputMode="decimal"
              pattern="[0-9]*\.?[0-9]{0,2}"
              className="price-input"
            />
            <span className="price-prefix">R</span>
          </div>
        </div>

        <div className="form-group date-group">
          <div>
            <label>Start Date <span className="required">(required)</span></label>
            <input
              type="date"
              name="startDate"
              value={formData.startDate}
              onChange={handleChange}
              required
            />
          </div>
          <div>
            <div className="end-date-row">
              <label>End Date <span className="required">(required unless extended)</span></label>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={noEndDate}
                  onChange={handleNoEndDateChange}
                  className="sr-only peer"
                />
                <div className="relative w-11 h-6 bg-gray-600 rounded-full peer peer-checked:bg-primary-teal peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-teal/30 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full"></div>
                <span className="ml-3 text-sm text-text-muted">Set end date 24 months from now</span>
              </label>
            </div>
            <input
              type="date"
              name="endDate"
              value={formData.endDate}
              onChange={handleChange}
              required={!noEndDate}
              disabled={noEndDate}
              className={noEndDate ? 'opacity-50 cursor-not-allowed' : ''}
            />
          </div>
        </div>

        <div className="form-group">
          <label>Categories</label>
          <Select
            isMulti
            options={categoryOptions}
            value={categoryOptions.filter((opt) => formData.categories.includes(opt.value))}
            onChange={(selected) => handleMultiSelectChange(selected, 'categories')}
            placeholder="Select categories"
            styles={dropdownStyles}
            filterOption={(option, input) => option.label.toLowerCase().includes(input.toLowerCase())}
          />
        </div>

        <div className="form-group">
          <label>Days of the Week</label>
          <Select
            isMulti
            options={dayOfWeekOptions}
            value={dayOfWeekOptions.filter((opt) => formData.dayOfWeek.includes(opt.value))}
            onChange={(selected) => handleMultiSelectChange(selected, 'dayOfWeek')}
            placeholder="Select days"
            styles={dropdownStyles}
            filterOption={(option, input) => option.label.toLowerCase().includes(input.toLowerCase())}
          />
        </div>

        <div className="form-group">
          <label>Type</label>
          <Select
            isMulti
            options={typeOptions}
            value={typeOptions.filter((opt) => formData.type.includes(opt.value))}
            onChange={(selected) => handleMultiSelectChange(selected, 'type')}
            placeholder="Select types"
            styles={dropdownStyles}
            filterOption={(option, input) => option.label.toLowerCase().includes(input.toLowerCase())}
          />
        </div>

        <div className="form-group">
          <label>Link <span className="example-url">(e.g., https://scover.co.za)</span></label>
          <input
            name="link"
            value={formData.link}
            onChange={handleChange}
            placeholder="Enter deal link"
          />
        </div>

        <div className="deal-form-buttons">
          <button type="submit" disabled={loading} className="submit-button">
            {loading ? 'Saving...' : isEdit ? 'Update Deal' : 'Create Deal'}
          </button>
          <button type="button" onClick={goBack} disabled={loading} className="cancel-button">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default DealForm;